import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { canOfferAutoTopup } from '@/lib/autoTopupOffer';
import useTranslation from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const createStyles = (colors) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 10,
    padding: 14,
    gap: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  textContainer: {
    flex: 1,
  },
});

/**
 * The standing auto top-up offer in the credit store (R-aug-11).
 *
 * Renders nothing unless this user may be asked — a Plus repeat buyer with a
 * saved Valmar card who has not turned auto top-up on, declined it recently, or
 * turned it off, and who is not in an Apple app or on Whop checkout. It only
 * ever opens the configuration screen; nothing is enabled from here.
 */
function AutoTopUpOfferRow() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const autoTopup = useAuthStore(state => state.user?.autoTopup);
  const { usesWhopCheckout } = useGabPurchase();
  const { t } = useTranslation();

  const handlePress = useCallback(() => {
    closeModal();
    openModal('AutoTopUpConfigModal');
  }, [closeModal, openModal]);

  if (!canOfferAutoTopup({ user: { autoTopup }, usesWhopCheckout })) return null;

  return (
    <Pressable
      style={styles.row}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={t('modals.autoTopUpOffer.title')}
      accessibilityHint={t('modals.autoTopUpOffer.subtitle')}
    >
      <Icon name="rotate" size={16} color={colors.text_color_primary} />
      <View style={styles.textContainer}>
        <Text size="s" weight="semibold" color="primary">
          {t('modals.autoTopUpOffer.title')}
        </Text>
        <Text size="xs" color="secondary">
          {t('modals.autoTopUpOffer.subtitle')}
        </Text>
      </View>
      <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />
    </Pressable>
  );
}

export default AutoTopUpOfferRow;
